import { useTu } from "@src/appV2/i18n/useTu";
import { Alert, Box, CircularProgress, Divider, Stack } from "@mui/material";
import { Text } from "@src/lib/ui-react";
import { ResourceSearchAutocomplete } from "../../../RequirementV2/components/ResourceSearchAutocomplete";
import { DynamicSchemaForm } from "../DynamicSchemaForm";
import { type EvidenceFormFieldProps } from "./useEvidenceForm";

export interface EvidenceFormViewProps extends EvidenceFormFieldProps {
  isLoading: boolean;
  isError: boolean;
}

export function EvidenceFormView(props: EvidenceFormViewProps) {
  const { tu } = useTu("evidence");

  const {
    control,
    errors,
    selectedDefinition,
    selectedOption,
    definitionOptions,
    searchInputValue,
    isSearchingDefinitions,
    hasSearchInput,
    propertySchema,
    hasProperties,
    onDefinitionChange,
    onSearchInputChange,
    isLoading,
    isError,
    isDefinitionMissing,
    isDefinitionSelectionPending,
  } = props;

  if (isLoading) {
    return (
      <>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Divider />
        <Alert severity="warning" sx={{ marginTop: 1 }}>
          {tu("Failed to load evidence definitions. Evidence properties cannot be edited.")}
        </Alert>
      </>
    );
  }

  if (isDefinitionMissing) {
    return (
      <>
        <Divider />
        <Alert severity="warning" sx={{ marginTop: 1 }}>
          {tu("Evidence definition not found. Evidence properties cannot be edited.")}
        </Alert>
      </>
    );
  }

  return (
    <>
      <Divider />
      <Text bold variant="subtitle1">
        {tu("Evidence")}
      </Text>
      <Box sx={{ marginTop: 4, marginBottom: 4, maxWidth: 400 }}>
        <ResourceSearchAutocomplete
          // An unfinished search (box text diverges from the committed selection)
          // blocks submit; flag the field so the divergence is visible.
          error={isDefinitionSelectionPending}
          isFetching={isSearchingDefinitions}
          label={tu("Evidence Definition")}
          loadingText={tu("Searching evidence definitions...")}
          noOptionsText={
            hasSearchInput
              ? tu("No evidence definitions found")
              : tu("Start typing to search evidence definitions")
          }
          options={definitionOptions}
          placeholder={tu("Search by name...")}
          value={selectedOption}
          inputValue={searchInputValue}
          onChange={onDefinitionChange}
          onInputChange={onSearchInputChange}
        />
      </Box>
      {selectedDefinition && propertySchema && hasProperties && (
        <Stack spacing={4} sx={{ marginBottom: 4 }}>
          <DynamicSchemaForm
            control={control}
            errors={errors}
            pathPrefix=""
            schema={propertySchema}
          />
        </Stack>
      )}
      {selectedDefinition && propertySchema && !hasProperties && (
        <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="body2">
          {tu("This definition has no properties to fill in.")}
        </Text>
      )}
    </>
  );
}
